import { useEffect, useState } from "react";
import { useProductSearch } from "@ludens-reklame/rubics-v3-react/product/hooks";
import { Page } from "@ludens-reklame/rubics-v3-sdk/page/types";
import { ProductSearchQuery } from "@ludens-reklame/rubics-v3-sdk/product/types";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import {
  Constrainer,
  HorizontalSpacer,
} from "../../internal-components/Layout.js";
import { Content, Filter, Layout, LoadMore, Top, Wrapper } from "./styles.js";
import { CheckBox } from "../../internal-components/Input/Checkbox.js";
import { ProductCard } from "../../internal-components/product/ProductCard.js";
import { Breadcrumbs } from "../../internal-components/Breadcrumbs.js";
import { Select } from "../../internal-components/Input/Select.js";
import { Button } from "../../internal-components/Button.js";
import { Busy } from "../../internal-components/Busy.js";
import { ProductGrid } from "../../internal-components/product/ProductGrid.js";
import { RichText } from "../../internal-components/RichText.js";

interface Props {
  component: "FilterableProducts" | "RelatedProducts";
  text?: string;
  categories?: { page?: Page }[];
}

export const FilterableProducts: React.FC<Props> = ({
  component,
  text,
  categories = [],
}) => {
  const isRelatedProducts = component === "RelatedProducts";
  const productStore = useStore().context.product;
  const _categories = categories.filter((c) => !!c.page);
  const [showFilterMobile, setShowFilterMobile] = useState<boolean>(false);
  const [queriedCategories, setQueriedCategories] = useState<string[]>([]);
  const [sorting, setSorting] = useState<InputSort | "">("");

  const productSearch = useProductSearch({}, { faceted: false });

  useEffect(() => {
    productSearch.search(
      false,
      isRelatedProducts && productStore
        ? { crossSell: productStore._id }
        : {
            categories:
              queriedCategories.length > 0
                ? queriedCategories
                : _categories.map((c) => c.page!._id),
            ...translateSorting(sorting || "standard"),
          }
    );
  }, [
    productSearch.search,
    isRelatedProducts,
    productStore,
    sorting,
    queriedCategories,
  ]);

  return (
    <Wrapper>
      <HorizontalSpacer>
        <Constrainer>
          {!isRelatedProducts && (
            <Top>
              <Breadcrumbs />
              <Select
                aria-label="Sorter"
                value={sorting}
                onChange={(e) => {
                  setSorting(e.target.value as any);
                }}
              >
                {!sorting && <option value="">Sorter</option>}
                <option value="standard">Standard</option>
                <option value="price:asc">Pris stigende</option>
                <option value="price:desc">Pris synkende</option>
              </Select>
            </Top>
          )}
          {text && (
            <RichText $centered dangerouslySetInnerHTML={{ __html: text }} />
          )}
          <Layout>
            {!isRelatedProducts && (
              <Filter $showOnMobile={showFilterMobile}>
                <h2 className="b3 desktop">Filter</h2>
                <h2 className="b3 mobile">
                  <a
                    href="#"
                    aria-label={
                      showFilterMobile ? "Skjul filter" : "Vis filter"
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      setShowFilterMobile(!showFilterMobile);
                    }}
                  >
                    Filter
                  </a>
                </h2>
                <ul>
                  {_categories.map((c) => {
                    return (
                      <li key={c.page!._id}>
                        <CheckBox
                          label={c.page!.title}
                          checked={queriedCategories.includes(c.page!._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setQueriedCategories([
                                ...queriedCategories,
                                c.page!._id,
                              ]);
                            } else {
                              setQueriedCategories(
                                queriedCategories.filter(
                                  (qc) => qc !== c.page!._id
                                )
                              );
                            }
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </Filter>
            )}
            <Content>
              <Busy $busy={productSearch.loading}>
                <ProductGrid $maxColumns={isRelatedProducts ? 4 : 3}>
                  {productSearch.results.map((product) => (
                    <ProductCard
                      key={product._id}
                      titleElement="h2"
                      product={product}
                    />
                  ))}
                </ProductGrid>
                {productSearch.hasMore && (
                  <LoadMore>
                    <Button
                      onClick={() => {
                        productSearch.search(true);
                      }}
                    >
                      {productSearch.loading ? "Laster…" : "Last inn flere"}
                    </Button>
                  </LoadMore>
                )}
              </Busy>
            </Content>
          </Layout>
        </Constrainer>
      </HorizontalSpacer>
    </Wrapper>
  );
};

type InputSort = "standard" | "price:asc" | "price:desc";

function translateSorting(
  sorting: InputSort
): Pick<ProductSearchQuery, "sort.price" | "sort.searchWeight"> {
  switch (sorting) {
    case "price:asc":
    case "price:desc":
      return {
        "sort.price": sorting === "price:asc" ? 1 : -1,
      };
    default:
      return {};
  }
}
